import { useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Flex, Grid, Modal } from "@mantine/core";
import { useForm } from "@mantine/form";
import { yupResolver } from "mantine-form-yup-resolver";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import React from "react";
import { franc } from "franc-min";

import {
  useCreateEvent,
  useUpdateEvent,
  useUpdateApplication,
} from "hooks/events";
import {
  queryDuplicateEvent,
  queryEventBySourceApplicationStart,
} from "supabase/events";
import {
  normalizeText,
  eventValidationSchema,
  firstCaptial,
  queryClient,
} from "utils";
import { useAppTranslation } from "hooks/useTranslation";
import { SendPushToBookmarks } from "../SendPushToBookmarks";
import { EventFormFieldId, EventFormProps, EventFormValues } from "./types";
import { validateEventTiming } from "./validateEventTiming";
import { initialFormValues, isFreeEventOf } from "./initialFormValues";
import { convertApplication, weeklyOccurrences } from "./convertApplication";
import { EventFormTitle } from "./EventFormTitle";
import { EventFormDescription } from "./EventFormDescription";
import { EventFormDateTime } from "./EventFormDateTime";
import { EventFormPrice } from "./EventFormPrice";
import { EventFormCategory } from "./EventFormCategory";
import { EventFormVenue } from "./EventFormVenue";
import { EventFormPicture } from "./EventFormPicture";
import { EventFormActions } from "./EventFormActions";
import { EventFormOrganiser } from "./EventFormOrganiser";
import { OrganizerForm } from "../../organizers/OrganizerForm";
import { VenueForm } from "../../venues/VenueForm";
import { mayDeskOverwrite } from "./deskOwnedValue";
import {
  buildSaveAsVenueRow,
  SaveAsVenueFormValues,
} from "./saveAsVenuePrefill";

export const EventForm = (props: EventFormProps) => {
  const {
    isNew = false,
    event,
    venues,
    organizers,
    tags,
    currOrganizerId,
    organizerVenues,
    isApplicationEdit = false,
    applicationOrganizerInfo,
    applicationVenueLocation,
    applicationContext,
    desk,
  } = props;
  const navigate = useNavigate();
  const { t, language } = useAppTranslation();

  // Get translated custom keyword
  const customVenueKeyword = t("venues.custom");

  // Determine field order based on language
  const isPtFirst = language === "pt-PT";

  // Both languages (AD-CR-01): the same rule as initialFormValues.isFreeEvent.
  const [isFreeEvent, setIsFreeEvent] = useState(isFreeEventOf(event));
  const [weekly, setWeekly] = useState(props.weekly ?? false);
  const [availableVenues, setAvailableVenues] = useState([] as typeof venues);
  // Track if the user has manually selected a tag
  const [userSelectedTag, setUserSelectedTag] = useState(false);

  const [file, setFile] = useState<File>();
  // True when `file` is a picture she uploaded or pasted, not a crop of the
  // image shown (DESK-07 'image', review AD-CR-03).
  const [fileUploaded, setFileUploaded] = useState(false);
  const setPictureFile = (next: File | undefined, origin: "crop" | "upload") => {
    setFile(next);
    setFileUploaded(next !== undefined && origin === "upload");
  };
  // useUpdateEvent's onSuccess receives no arguments, so the desk's apply
  // callback reads the values that were submitted from here.
  const submittedValuesRef = useRef<EventFormValues | null>(null);
  const onDeskUpdated = desk?.onUpdated;
  const updateEvent = useUpdateEvent(
    onDeskUpdated
      ? () => {
          if (submittedValuesRef.current) {
            onDeskUpdated(submittedValuesRef.current);
          }
        }
      : () => navigate(-1)
  );
  // FBIMP-13: per-event navigation is suppressed on the conversion path so a
  // weekly application does not fire navigate(-1) once per occurrence.
  // `convertApplication` navigates once, after the processed write.
  const createEvent = useCreateEvent({ navigateOnSuccess: !isApplicationEdit });
  const updateApplication = useUpdateApplication();

  const [showPushModal, setShowPushModal] = useState(false);
  // Desk approve: the button stays busy across beforeConvert and the desk's
  // processed write, not only while an insert is in flight.
  const [deskSubmitting, setDeskSubmitting] = useState(false);
  const [missingPicture, setMissingPicture] = useState(false);

  // State to store the selected organizer
  const [selectedOrganizer, setSelectedOrganizer] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // State to store the id of a newly created organizer for pending selection
  const [pendingOrganizerId, setPendingOrganizerId] = useState<number | null>(
    null
  );

  // 33.3 D-06: the venue created by "Save as venue", selected once the
  // refreshed venues list contains it (Pitfall P2: the submit resolves the
  // venue from that list).
  const [pendingVenueId, setPendingVenueId] = useState<number | null>(null);
  // The id of the venue "Save as venue" selected. Venue names are not unique
  // across towns, so the submit resolves this id first and the name second.
  // 33.4 (PATTERNS Pitfall 9): seeded with the pre-filled venue's id, so a
  // venue the editor linked by id is submitted (and reported to the desk) as
  // that id, not as the first venue of another town with the same name.
  const selectedVenueIdRef = useRef<number | null>(event?.venues?.id ?? null);

  // Modal manager state
  const [modal, setModal] = useState<null | { type: string; data?: any }>(null);
  // Create a form without validation initially
  const form = useForm<EventFormValues>({
    // One pure transform of the row (initialFormValues.ts), shared with the
    // desk's DESK-07 baseline so the two can never disagree.
    initialValues: initialFormValues(event, {
      tags,
      language,
      isApplicationEdit,
      customVenueKeyword,
    }),
    validate: (values) => {
      // Initialize an empty errors object
      const errors: Record<string, string> = {};

      // For both draft and published: require at least one title (EN or PT)
      if (
        (!values.title || values.title.trim() === "") &&
        (!values.title_pt || values.title_pt.trim() === "")
      ) {
        errors.title = t("errors.title_required");
      } else if (
        values.title &&
        values.title.trim() !== "" &&
        values.title.length < 2
      ) {
        errors.title = t("errors.title_too_short");
      } else if (
        values.title_pt &&
        values.title_pt.trim() !== "" &&
        values.title_pt.length < 2
      ) {
        errors.title_pt = t("errors.title_pt_too_short");
      }

      // Only validate other fields if the event is being published and not an application edit
      if (values.published) {
        // Check if mainTag is selected for published events
        if (!values.mainTag) {
          errors.mainTag = t("errors.category_required");
        }

        // Check if venue is selected when currOrganizerId exists
        if (currOrganizerId && (!values.venue || values.venue === "")) {
          errors.venue = t("errors.venue_required");
        }

        // Validate custom venue fields
        if (values.venue === customVenueKeyword) {
          if (!values.venue_name || values.venue_name.trim() === "") {
            errors.venue_name = t("errors.venue_name_required");
          }
          if (
            values.city_id === null &&
            (!values.venue_city || values.venue_city.trim() === "")
          ) {
            errors.venue_city = t("errors.venue_city_required");
          }
          if (!values.venue_location || values.venue_location.trim() === "") {
            errors.venue_location = t("errors.venue_location_required");
          }
        }

        const resolver = yupResolver(eventValidationSchema);
        return {
          ...errors,
          ...resolver(values as unknown as Record<string, unknown>),
        };
      }
      return errors; // For drafts, only validate title presence
    },
  });

  // 33.3 D-03b: the last venue location the desk wrote. The desk prefill's
  // location is the desk's own value, so it seeds the ref; a typed value or a
  // dragged pin moves the field away from it, which makes it hers.
  const lastDeskLocationRef = useRef<string | null>(
    desk ? form.getValues().venue_location || null : null
  );

  // Reset userSelectedTag when a new event is loaded
  useEffect(() => {
    setUserSelectedTag(false);
  }, [event?.id]);

  // Update the mainTag display value when language changes
  useEffect(() => {
    if (form.values.mainTag) {
      if (userSelectedTag) {
        // If user selected a tag, translate that tag instead of reverting to event tag
        const selectedTagObj = tags.find((tag) =>
          language === "pt-PT"
            ? tag.tag === form.values.mainTag ||
              tag.tag_pt === form.values.mainTag
            : tag.tag === form.values.mainTag ||
              (tag.tag_pt && tag.tag_pt === form.values.mainTag)
        );

        if (selectedTagObj) {
          const newDisplayValue =
            language === "pt-PT"
              ? selectedTagObj.tag_pt || selectedTagObj.tag
              : selectedTagObj.tag;

          if (newDisplayValue !== form.values.mainTag) {
            form.setFieldValue("mainTag", newDisplayValue);
          }
        }
      } else if (event?.main_tag) {
        // Original behavior for event tags
        const selectedTag = tags.find((tag) => tag.id === event.main_tag);
        if (selectedTag) {
          const newDisplayValue =
            language === "pt-PT"
              ? selectedTag.tag_pt || selectedTag.tag
              : selectedTag.tag;

          // Only update if the display value is different
          if (newDisplayValue !== form.values.mainTag) {
            form.setFieldValue("mainTag", newDisplayValue);
          }
        }
      }
    }
  }, [language, event, tags, userSelectedTag, form]);

  // Desk image swap (UI-SPEC §D): the picture follows the override without a
  // remount of the form, so her other edits survive. `form` is deliberately
  // not a dependency: it is a new object on every render, and setting the
  // field each render would loop.
  //
  // A crop (or upload) made before the swap belongs to the OLD image: it is
  // dropped, or `newPicture` would publish it over the swapped one (review
  // AD-CR-02). The picture control is also remounted (its key below), so it
  // shows the new image and its cropper fetches the new one.
  const pictureOverride = desk?.pictureOverride;
  useEffect(() => {
    if (pictureOverride !== null && pictureOverride !== undefined) {
      form.setFieldValue("picture", pictureOverride);
      setFile(undefined);
      setFileUploaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pictureOverride]);

  // The desk's automatic translation (spec-desk-auto-translate D-04): it
  // arrives after the form rendered and fills only the fields that are still
  // empty, so anything she typed meanwhile stays. `form` is not a dependency
  // for the same reason as above: only a new fill object should run this.
  const translationFill = desk?.translationFill;
  useEffect(() => {
    if (!translationFill) return;
    const current = form.getValues();
    (
      Object.entries(translationFill) as [
        keyof NonNullable<typeof translationFill>,
        string | undefined
      ][]
    ).forEach(([key, value]) => {
      if (value === undefined) return;
      if (String(current[key] ?? "").trim() !== "") return;
      form.setFieldValue(key, value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationFill]);

  // 33.3 D-03b: report the town and start to the desk on mount and on every
  // change, so it can re-run its lookups (it debounces). `form` is not a
  // dependency, as above.
  // 33.1 D-29: it also carries the re-check's text and end date (matchFields).
  const onWatchedChange = desk?.onWatchedChange;
  // A Date is a new object on each change; its time is the comparable value.
  const watchedStartMs = form.values.startTime?.getTime();
  useEffect(() => {
    onWatchedChange?.({
      cityId: form.values.city_id,
      cityText: form.values.venue_city,
      startTime: form.values.startTime,
      matchFields: {
        finishTime: form.values.finishTime,
        title: form.values.title,
        title_pt: form.values.title_pt,
        subTitle: form.values.subTitle,
        subTitle_pt: form.values.subTitle_pt,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.city_id, form.values.venue_city, watchedStartMs]);

  // 33.3 WR-02: report the venue name on mount and on every change, so the
  // desk's history match and Save-as-venue offer follow what she typed.
  const onVenueNameChange = desk?.onVenueNameChange;
  useEffect(() => {
    onVenueNameChange?.(form.values.venue_name ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.venue_name]);

  // 33.4 D-15 / D-24: report the selected venue on mount and on every change,
  // with its id resolved like the submit's (the selected id first, then the
  // name), so the desk compares ids and a name repeated across towns never
  // confuses it. Also on a venues refresh: a created venue gets its id then.
  const onVenueChange = desk?.onVenueChange;
  useEffect(() => {
    if (!onVenueChange) return;
    const name = form.values.venue ?? "";
    const id =
      name === customVenueKeyword || name === ""
        ? null
        : venues.find(
            (v) => v.id === selectedVenueIdRef.current && v.name === name
          )?.id ??
          venues.find((v) => v.name === name)?.id ??
          null;
    onVenueChange({ name, id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.venue, venues]);

  // 33.3 D-03b: a town re-run's location. It is written only into an empty
  // field or over the desk's own last value, never over hers, and only the
  // location: the address stays (DESK-07 baseline, Pitfall P4).
  const locationFill = desk?.locationFill;
  useEffect(() => {
    if (!locationFill) return;
    const current = form.getValues().venue_location;
    if (!mayDeskOverwrite(current, lastDeskLocationRef.current)) return;
    form.setFieldValue("venue_location", locationFill.venue_location);
    lastDeskLocationRef.current = locationFill.venue_location || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFill]);

  // 33.3 D-06: select the venue "Save as venue" created once the refreshed
  // list contains it, and drop the custom-venue fields it replaces.
  useEffect(() => {
    if (pendingVenueId === null) return;
    const found = venues.find((v) => v.id === pendingVenueId);
    if (!found) return;
    form.setFieldValue("venue", found.name ?? "");
    form.setFieldValue("venue_name", "");
    form.setFieldValue("venue_address", "");
    form.setFieldValue("venue_description", "");
    form.setFieldValue("venue_location", "");
    selectedVenueIdRef.current = found.id;
    setPendingVenueId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVenueId, venues]);

  // Desk approve copies the (possibly swapped) form picture into the new
  // event. Everywhere else, including desk apply, the event's own stored
  // picture stays the one shown and saved: updateEvent deletes currPicture
  // from the events bucket when a new file is uploaded.
  const followsFormPicture = desk?.mode === "approve";

  const handleSetIsFreeEvent = (value: boolean) => {
    setIsFreeEvent(value);
    form.setFieldValue("isFreeEvent", value);
  };

  // Re-entry guard (review AD-WR-02): a double click or a second Enter must
  // not start a second submit while the first is still awaiting its reads.
  // A ref, not state: it is set synchronously, before the first await, so
  // the second click sees it even before React re-renders the busy button.
  const submittingRef = useRef(false);
  const formSubmit = async (values: EventFormValues) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    // Desk: the button is busy from the click, across the duplicate read,
    // beforeConvert and the desk's processed write.
    if (desk) setDeskSubmitting(true);
    try {
      await submitValues(values);
    } finally {
      submittingRef.current = false;
      if (desk) setDeskSubmitting(false);
    }
  };

  const submitValues = async (values: EventFormValues) => {
    // Pre-conversion gate (FBIMP-13, spec 10.3). On the create-or-convert path
    // require a title AND a valid start, a valid end, and an end after the
    // start. On a plain edit apply the title rule alone, so a legacy event
    // whose start_time is null in the database can still be saved.
    const timingErrors = validateEventTiming({
      title: values.title,
      title_pt: values.title_pt,
      startTime: values.startTime,
      finishTime: values.finishTime,
    });
    const blockingErrors =
      isNew || isApplicationEdit
        ? timingErrors
        : timingErrors.title
        ? { title: timingErrors.title }
        : {};

    const blockingEntries = Object.entries(blockingErrors);
    if (blockingEntries.length > 0) {
      // Name EVERY offending field, not just the first — the requirement is
      // that a blocked conversion names the missing fields, plural.
      blockingEntries.forEach(([field, key]) => {
        form.setFieldError(field, t(key));
      });
      return;
    }

    // Check if picture is missing only for published events
    if (values.published && !file && (!event?.picture || isApplicationEdit)) {
      setMissingPicture(true);
      return;
    }
    setMissingPicture(false);

    // Check for duplicate event (same title and start time).
    // Skipped ENTIRELY while the start time is missing. queryDuplicateEvent
    // calls start_time.toISOString() unguarded, so a null throws into the
    // catch below, which logs and lets the submit continue — turning the
    // duplicate gate into a silent no-op for exactly the inputs it cannot
    // evaluate. After the timing gate above the create path always has a
    // valid date; this guard covers the edit path and is defence in depth.
    const startTime = values.startTime;
    const hasValidStartTime =
      startTime instanceof Date && !Number.isNaN(startTime.getTime());

    if (hasValidStartTime) {
      try {
        const duplicateEvent = await queryDuplicateEvent({
          title: values.title || '',
          title_pt: values.title_pt || '',
          start_time: startTime,
          excludeEventId: isNew || isApplicationEdit ? undefined : event?.id
        });

        if (duplicateEvent) {
          form.setFieldError('title', t('errors.event_already_exists'));
          return;
        }
      } catch (error) {
        console.error('Error checking for duplicate event:', error);
      }
    }

    const editedEvent = {
      title: firstCaptial(values.title)
        ? values.title.charAt(0).toUpperCase() + values.title.slice(1)
        : values.title,
      sub_title: values.subTitle,
      organizer_id:
        currOrganizerId ||
        selectedOrganizer?.id ||
        (typeof values.user === "number" ? values.user : null) ||
        null,
      description: firstCaptial(values.description).replace("+351", ""),
      title_pt: firstCaptial(values.title_pt),
      sub_title_pt: values.subTitle_pt,
      description_pt: firstCaptial(values.description_pt).replace("+351", ""),
      price: isFreeEvent ? "" : values.price,
      price_pt: isFreeEvent ? "" : values.price_pt,
      start_time: values.startTime,
      finish_time: values.finishTime,
      currPicture: followsFormPicture
        ? values.picture || ""
        : event?.picture || "",
      newPicture: file,
      tags: values.mainTag
        ? [
            Number(
              tags.find((tag) =>
                language === "pt-PT"
                  ? tag.tag_pt === values.mainTag || tag.tag === values.mainTag
                  : tag.tag === values.mainTag
              )!.id
            ),
          ]
        : [],
      main_tag: values.mainTag
        ? tags.find((tag) =>
            language === "pt-PT"
              ? tag.tag_pt === values.mainTag || tag.tag === values.mainTag
              : tag.tag === values.mainTag
          )!.id
        : null,
      registration_link: values.registrationLink
        ?.toLowerCase()
        ?.startsWith("www")
        ? `http://${values.registrationLink}`
        : values.registrationLink,
      venue:
        values.venue === customVenueKeyword
          ? null
          : // By the id "Save as venue" selected first: names are not
            // unique across towns (Pitfall P2). A venue she picked by name
            // later no longer matches the ref, so the name lookup applies.
            venues.find(
              (v) =>
                v.id === selectedVenueIdRef.current && v.name === values.venue
            )?.id ?? venues.find((v) => v.name === values.venue)!.id,
      venue_name:
        values.venue === customVenueKeyword ? values.venue_name : null,
      venue_address:
        values.venue === customVenueKeyword ? values.venue_address : null,
      // Canonical city: write only city_id — a DB trigger syncs venue_city.
      // Unknown city (escape hatch): write the free text to venue_city with
      // city_id null — the trigger keeps it and queues it for review.
      city_id: values.venue === customVenueKeyword ? values.city_id : null,
      venue_city:
        values.venue === customVenueKeyword && values.city_id === null
          ? values.venue_city || null
          : null,
      venue_description:
        values.venue === customVenueKeyword ? values.venue_description : null,
      venue_location_lat:
        values.venue === customVenueKeyword && values.venue_location
          ? Number(values.venue_location.split(",")[0]) || null
          : null,
      venue_location_long:
        values.venue === customVenueKeyword && values.venue_location
          ? Number(values.venue_location.split(",")[1]) || null
          : null,
      published: values.published === undefined ? false : values.published,
      cancelled: values.cancelled === undefined ? false : values.cancelled,
      is_recommended:
        values.isRecommended === undefined ? false : values.isRecommended,
      made_by_organizer: !!currOrganizerId,
    };
    // The timing gate at the head of this function has already returned when
    // either date is missing or invalid on the create-or-convert path, so both
    // are real dates by the time either branch below reads them.
    const occurrenceStart = values.startTime as Date;
    const occurrenceFinish = values.finishTime as Date;

    if (isApplicationEdit) {
      // Refuse rather than fall back. Converting without a context would write
      // events with no source_application_id, which are invisible to the
      // (source_application_id, start_time) index — a later retry would
      // duplicate them instead of reusing them.
      if (!applicationContext) {
        form.setFieldError("title", t("common.error"));
        return;
      }

      const context = applicationContext;
      // `start_time` and `finish_time` are dropped here on purpose: the
      // orchestrator owns the times, one per occurrence.
      const { start_time, finish_time, ...baseEvent } = editedEvent;

      const deskMarkProcessed = desk?.markProcessed;
      try {
        // Desk approve: re-check the candidate before anything is written
        // (T-33-43). A false result aborts with no event created.
        if (desk?.beforeConvert) {
          const proceed = await desk.beforeConvert();
          if (!proceed) return;
        }
        await convertApplication({
          occurrences: weekly
            ? weeklyOccurrences(occurrenceStart, occurrenceFinish)
            : [{ start_time: occurrenceStart, finish_time: occurrenceFinish }],
          baseEvent,
          applicationContext: context,
          createEvent: createEvent.mutateAsync,
          findExistingOccurrence: (occurrence) =>
            queryEventBySourceApplicationStart({
              sourceApplicationId: context.id,
              startTime: occurrence.start_time,
            }),
          // Desk approve injects its own decision write (D-05) and is handed
          // the submitted values to record the fields she edited (D-09).
          markProcessed: deskMarkProcessed
            ? (id) =>
                deskMarkProcessed(id, values, { weekly, uploaded: fileUploaded })
            : (id) => updateApplication.mutateAsync({ id, processed: true }),
          // Once, at the end. Not navigate(-1): the application editor is
          // reachable by deep link now (FBIMP-11), and going back one entry
          // from a deep link leaves the console entirely.
          navigate: () => {
            // The processed screen lists what the application produced from
            // ["events", "byApplication", id]. A retry that reuses every
            // existing occurrence creates nothing, so no createEvent success
            // invalidates that key on its behalf.
            queryClient.invalidateQueries({
              queryKey: ["events", "byApplication", context.id],
            });
            // The desk stays on its own page and moves on by itself.
            if (desk) {
              desk.onApproved?.();
              return;
            }
            navigate("/applications");
          },
        });
      } catch (error: any) {
        // The desk owns the copy for a failed approve (e.g. Already decided).
        if (desk?.onApproveError) {
          desk.onApproveError(error);
          return;
        }
        notifications.show({
          title: t("common.error"),
          message: t("errors.conversion_failed", {
            message: error?.message ?? "",
          }),
          color: "red",
        });
        return;
      }
    } else if (isNew) {
      // Manual creation is unchanged: fire-and-forget per occurrence, and the
      // hook still navigates. Only the weekly expansion is shared, so there is
      // one definition of "weekly" rather than two that can drift.
      const occurrences = weekly
        ? weeklyOccurrences(occurrenceStart, occurrenceFinish)
        : [{ start_time: occurrenceStart, finish_time: occurrenceFinish }];

      occurrences.forEach((occurrence) => {
        createEvent.mutate({
          ...editedEvent,
          start_time: occurrence.start_time as any,
          finish_time: occurrence.finish_time as any,
        } as any);
      });
    } else {
      // Desk apply: re-check the candidate before the event changes
      // (AD-WR-10). A false result aborts with nothing written.
      if (desk?.beforeUpdate) {
        const proceed = await desk.beforeUpdate();
        if (!proceed) return;
      }
      submittedValuesRef.current = values;
      const onUpdateError = desk?.onUpdateError;
      updateEvent.mutate(
        {
          id: event!.id,
          uid: event!.uid,
          ...editedEvent,
        } as any,
        // Desk apply: a failed save is never silent (AD-WR-07). Every other
        // caller keeps today's behaviour.
        onUpdateError ? { onError: (error) => onUpdateError(error) } : undefined
      );
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: keyof EventFormValues
  ) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");

    const { selectionStart, selectionEnd } = e.currentTarget;

    // Insert the plain text at the cursor position
    const newValue =
      (form.values[field] as string).substring(0, selectionStart || 0) +
      normalizeText(text) +
      (form.values[field] as string).substring(selectionEnd || 0);
    form.setFieldValue(field, newValue);
  };

  // Set available venues based on whether currOrganizerId exists
  useEffect(() => {
    if (currOrganizerId && organizerVenues) {
      // If organizer is logged in, only show their linked venues
      setAvailableVenues(organizerVenues);
    } else {
      // Otherwise show all venues
      setAvailableVenues(venues);
    }
  }, [currOrganizerId, venues, organizerVenues]);

  // Helper to detect language
  const detectLanguage = (text: string) => {
    if (!text || text.trim().length < 10) return null;
    const langCode = franc(text);
    if (langCode === "por") return "portuguese";
    if (langCode === "eng") return "english";
    return null;
  };

  // Keep weekly state in sync with prop if it changes
  useEffect(() => {
    if (typeof props.weekly === "boolean") {
      setWeekly(props.weekly);
    }
  }, [props.weekly]);

  // Desk field wrappers (flags, provenance, scroll anchors). Without
  // desk.renderField both are the identity, so the layout is untouched.
  const renderField = desk?.renderField;
  const wrapField = (field: EventFormFieldId, node: ReactNode) =>
    renderField ? renderField(field, node) : node;
  // A section made of Grid.Col siblings keeps its columns inside a full-width
  // column of its own, so the wrapper never becomes a bare Grid child.
  const wrapGridSection = (field: EventFormFieldId, cols: ReactNode) =>
    renderField ? (
      <Grid.Col span={12}>{renderField(field, <Grid>{cols}</Grid>)}</Grid.Col>
    ) : (
      cols
    );

  return (
    <>
      <form onSubmit={form.onSubmit(formSubmit)}>
        <Flex direction="column" justify="space-between" h="calc(100vh - 50px)">
          <Flex
            direction="column"
            w="100%"
            h="calc(100vh - 100px)"
            style={{ overflowY: "auto" }}
          >
            <Grid>
              {!currOrganizerId &&
                wrapGridSection(
                  "organizer",
                  <>
                    <EventFormOrganiser
                      form={form}
                      organizers={organizers}
                      currOrganizerId={currOrganizerId}
                      applicationOrganizerInfo={applicationOrganizerInfo}
                      t={t}
                      language={language}
                      onOrganizerSelect={setSelectedOrganizer}
                      onAddOrganizerClick={(info) =>
                        setModal({ type: "organizer", data: info })
                      }
                      pendingOrganizerId={pendingOrganizerId}
                      onPendingOrganizerHandled={() =>
                        setPendingOrganizerId(null)
                      }
                      disableAiMatch={desk?.disableAiMatch === true}
                    />
                    <Grid.Col span={6}></Grid.Col>
                  </>
                )}

              <EventFormTitle
                form={form}
                isPtFirst={isPtFirst}
                t={t}
                handlePaste={handlePaste}
                wrapSection={renderField ? wrapGridSection : undefined}
              />

              {wrapGridSection(
                "description",
                <EventFormDescription
                  form={form}
                  isPtFirst={isPtFirst}
                  t={t}
                  handlePaste={handlePaste}
                />
              )}
            </Grid>
            <Grid>
              <Grid.Col span={6}>
                <EventFormDateTime
                  form={form}
                  t={t}
                  isNew={isNew || isApplicationEdit}
                  weekly={weekly}
                  setWeekly={setWeekly}
                  currOrganizerId={currOrganizerId}
                  wrapField={renderField ? wrapField : undefined}
                />

                {wrapField(
                  "price",
                  <EventFormPrice
                    form={form}
                    isPtFirst={isPtFirst}
                    t={t}
                    isFreeEvent={isFreeEvent}
                    handleSetIsFreeEvent={handleSetIsFreeEvent}
                    handlePaste={handlePaste}
                  />
                )}

                {wrapField(
                  "category",
                  <EventFormCategory
                    form={form}
                    t={t}
                    language={language}
                    tags={tags}
                    setUserSelectedTag={setUserSelectedTag}
                  />
                )}


                {wrapField(
                  "place",
                  <EventFormVenue
                    form={form}
                    t={t}
                    customVenueKeyword={customVenueKeyword}
                    availableVenues={availableVenues}
                    handlePaste={handlePaste}
                    venues={venues}
                    applicationVenueLocation={applicationVenueLocation}
                    disableAiMatch={desk?.disableAiMatch === true}
                    mapFallbackCenter={desk?.mapFallbackCenter ?? null}
                    locationNote={desk?.venueLocationNote ?? null}
                    onSaveAsVenue={
                      // WR-01: hidden while the venue just created waits to
                      // appear in the list, so a second click cannot create
                      // a second row.
                      desk?.saveAsVenue && pendingVenueId === null
                        ? () => setModal({ type: "venue" })
                        : undefined
                    }
                    similarVenues={
                      pendingVenueId === null
                        ? desk?.similarListedVenues ?? []
                        : []
                    }
                    // UAT 2026-11-02: the listed venue is already in the
                    // list, so the same pending-venue effect selects it and
                    // drops the custom-venue fields.
                    onUseVenue={
                      desk?.similarListedVenues ? setPendingVenueId : undefined
                    }
                    venueAssist={desk?.venueAssist ?? null}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={6}>
                {wrapField(
                  "image",
                  <EventFormPicture
                    // Desk approve: a new picture is a new control (AD-CR-02),
                    // so EditPicture's cached crop and source file go too.
                    key={followsFormPicture ? form.values.picture : "event"}
                    eventPicture={
                      (followsFormPicture ? form.values.picture : event?.picture) ||
                      undefined
                    }
                    setFile={setPictureFile}
                    missingPicture={missingPicture}
                    setMissingPicture={setMissingPicture}
                    t={t}
                    isApplicationEdit={isApplicationEdit}
                    // `?? undefined` rather than passing the null through: the
                    // prop is "absent" all the way down, and absent is what makes
                    // the cropper behave as it always has. Manual event creation
                    // passes no applicationContext at all and lands here too.
                    initialCroppedAreaPixels={
                      applicationContext?.artworkRect ?? undefined
                    }
                  />
                )}
              </Grid.Col>
            </Grid>
          </Flex>
          <EventFormActions
            form={form}
            t={t}
            isNew={isNew}
            isPending={
              updateEvent.isPending || createEvent.isPending || deskSubmitting
            }
            currOrganizerId={currOrganizerId}
            navigate={navigate}
            setShowPushModal={setShowPushModal}
            isApplication={isApplicationEdit}
            submitLabel={desk?.submitLabel}
            backLabel={desk?.backLabel}
            onBack={desk?.onBack}
            hidePushButton={desk?.hidePushButton}
          />
        </Flex>
      </form>
      {!desk?.hidePushButton && (
        <SendPushToBookmarks
          visible={showPushModal}
          eventId={event?.id!}
          onClose={() => {
            setShowPushModal(false);
          }}
        />
      )}
      {/* Modal manager for popups */}
      <Modal
        opened={modal?.type === "organizer"}
        onClose={() => setModal(null)}
        title={t("organizers.create_organizer")}
        size="xl"
        centered
      >
        {modal?.type === "organizer" &&
          (() => {
            const message = applicationOrganizerInfo?.organiserMessage || "";
            const detectedLang = detectLanguage(message);
            return (
              <OrganizerForm
                isNew
                isApplication={!!applicationOrganizerInfo}
                organizer={{
                  id: 0,
                  uid: null,
                  created_at: "",
                  name: applicationOrganizerInfo?.organiserName || "",
                  email: applicationOrganizerInfo?.organiserEmail || "",
                  phone: applicationOrganizerInfo?.organiserPhone || "",
                  description: detectedLang === "portuguese" ? "" : message,
                  description_pt: detectedLang === "portuguese" ? message : "",
                  whatsapp: "",
                  preferred_language:
                    detectedLang === "portuguese" ? "pt" : "en",
                  is_municipality: false,
                  enable_weekly: false,
                  website: "",
                  picture: applicationOrganizerInfo?.organiserLogo || null,
                  // Slugs are assigned server-side once the organizer exists
                  slug_en: null,
                  slug_pt: null,
                  // All nullable fields set to null or empty
                }}
                onCreated={(org) => {
                  setModal(null);
                  setPendingOrganizerId(org.id);
                }}
              />
            );
          })()}
      </Modal>
      {/* 33.3 D-06: outside the event <form>, because a submit inside a
          portal still bubbles along the React tree and would Approve. */}
      <Modal
        opened={modal?.type === "venue"}
        onClose={() => setModal(null)}
        title={t("desk.venue_location.save_as_venue_title")}
        size="xl"
        centered
      >
        {modal?.type === "venue" && desk?.saveAsVenue && (
          <VenueForm
            isNew
            venue={buildSaveAsVenueRow(
              form.getValues() as SaveAsVenueFormValues,
              desk.saveAsVenue
            )}
            submitLabel={t("desk.venue_location.save_as_venue_submit")}
            onCreated={(created) => {
              setModal(null);
              setPendingVenueId(created.id);
            }}
            // CR-01: a listed venue of that name and town already exists, so
            // select it rather than create a second row (two rows make the
            // desk's listed-venue match ambiguous for every later candidate).
            findExisting={desk.findListedVenue}
            // WR-08: a venue without a city_id would never be matched.
            requireListedCity
            onExisting={(existing) => {
              setModal(null);
              setPendingVenueId(existing.id);
              notifications.show({
                color: "blue",
                message: t("desk.venue_location.save_as_venue_existing", {
                  name: existing.name,
                }),
              });
            }}
          />
        )}
      </Modal>
    </>
  );
};
